import cn from "classnames";
import Address from "components/shared/address";
import SyncStatusInfo from "components/shared/syncStatusInfo";
import { getWaybillTitleStatusInfo } from "components/waybills/status";
import { cargosWithTemperature, counterpartyInsolvencyTooltip } from "helpers/bid";
import { formatDateTimezone, getShortAddress } from "helpers/bidpoint";
import { dateTimeToView, humanizedTimeRange } from "helpers/date";
import {
    bidCalculationStatuses,
    bidCalculationStatusesEnum,
    bidPaymentStatuses,
    bidPaymentStatusesEnum,
    bidStateFlags,
    bidStateFlagsEnum,
    featureModuleTypesEnum,
    syncEntityTypesEnum,
} from "helpers/enums";
import { bidPointLoadUnloadStatuses } from "helpers/enums/bidPointLoadUnloadStatuses";
import { bidStatuses, bidStatusesEnum } from "helpers/enums/bidStatuses";
import { HiddenDataWrapper, hiddenUserData } from "helpers/hiddenData";
import { paths } from "helpers/paths";
import { enumLabel, flagVal, formatNumber, ymEvent } from "helpers/system";
import Tooltip from "rc-tooltip";
import React, { Component } from "react";
import { Link } from "react-router-dom";

const hasUploadedFileFlagVal = flagVal(bidStateFlags, bidStateFlagsEnum.HasUploadedFile);
const hasPlannedRefuelingsFlagVal = flagVal(bidStateFlags, bidStateFlagsEnum.HasPlannedRefuelings);
const maxNumberOfRefuelingsReachedVal = flagVal(bidStateFlags, bidStateFlagsEnum.MaxNumberOfRefuelingsReached);
const carTemperatureRegimeDeviatedFlagVal = flagVal(bidStateFlags, bidStateFlagsEnum.CarTemperatureRegimeDeviated);
const trailerTemperatureRegimeDeviatedFlagVal = flagVal(
    bidStateFlags,
    bidStateFlagsEnum.TrailerTemperatureRegimeDeviated,
);
const hasAutoCorrectedPointFlagVal = flagVal(bidStateFlags, bidStateFlagsEnum.HasAutoCorrectedPoint);
const MARKET_RATE_THRESHOLD_PERCENT = 5;

export default class Bid extends Component {
    isCargo = () => {
        const { isCargoOwnerAdmin, isCargoOwnerManager } = this.props.roles;
        return isCargoOwnerAdmin || isCargoOwnerManager;
    };

    renderParticipants = () => {
        const { participantsCount, isAccepted } = this.props.item;
        if (this.isCargo() && isAccepted === false) {
            const pluralWord = participantsCount === 1 ? "отклик" : participantsCount < 5 ? "отклика" : "откликов";
            const text = participantsCount ? `${participantsCount} ${pluralWord}` : "Нет откликов";
            return <span>{text}</span>;
        }
        return null;
    };

    renderStatus = () => {
        if (this.props.item.isDeleted) {
            return <span className="badge badge-dark">Удалена</span>;
        }
        const {
            status,
            bidPointLoadUnloadStatus,
            calculationStatus,
            isEmptyMileageBid,
            isApproved,
            isCreatedByOperator,
            acceptedByDriverAt,
            firstLatePeriod,
            isPreBid,
        } = this.props.item;
        const { loadUnloadedAtByLogist, autoLeftAt, loadUnloadedAt } = this.props.item.lastBidPoint || {};
        let textStatus = bidStatuses.find((item) => item.value === status);
        let statusCname = cn("badge", {
            "badge-light": status === bidStatusesEnum.New,
            "badge-primary": status === bidStatusesEnum.Started,
            "badge-secondary": status === bidStatusesEnum.Canceled,
            "badge-success": status === bidStatusesEnum.Done,
        });
        let statusTip = null;
        if (status === bidStatusesEnum.Started) {
            if (calculationStatus) {
                statusCname =
                    calculationStatus === bidCalculationStatusesEnum.InProgress
                        ? "badge badge-info"
                        : "badge badge-danger";
                textStatus = bidCalculationStatuses.find((item) => item.value === calculationStatus);

                statusTip =
                    calculationStatus === bidCalculationStatusesEnum.NoCoordinates
                        ? "Заявка не учтена. Нет данных GPS мониторинга."
                        : calculationStatus === bidCalculationStatusesEnum.NoActivity
                          ? "Проверьте корректность данных."
                          : null;
            } else if (bidPointLoadUnloadStatus) {
                textStatus = bidPointLoadUnloadStatuses.find((item) => item.value === bidPointLoadUnloadStatus);
                statusCname = "badge badge-info";
            }

            return (
                <>
                    {statusTip ? (
                        <Tooltip overlay={statusTip} placement="top">
                            <span className={statusCname}>{textStatus && textStatus.label}</span>
                        </Tooltip>
                    ) : (
                        <span className={statusCname}>{textStatus && textStatus.label}</span>
                    )}
                    {!calculationStatus && firstLatePeriod && firstLatePeriod.charAt(0) === "-" && (
                        <span>
                            <br />
                            <Tooltip
                                overlay={`Опоздание на ближайшую точку: ${humanizedTimeRange(firstLatePeriod.slice(1))}`}
                                placement="top"
                            >
                                <span className="badge badge-danger">
                                    {humanizedTimeRange(firstLatePeriod.slice(1))}
                                </span>
                            </Tooltip>
                        </span>
                    )}
                    {isEmptyMileageBid && (
                        <span>
                            <br />
                            <span className="badge badge-warning">Порожняя</span>
                        </span>
                    )}
                    {acceptedByDriverAt && (
                        <span>
                            <br />
                            <Tooltip overlay={dateTimeToView(acceptedByDriverAt)} placement="top">
                                <span className="badge badge-info">Принята водителем</span>
                            </Tooltip>
                            <br />
                        </span>
                    )}
                </>
            );
        } else {
            return (
                <>
                    <span className={statusCname}>{textStatus && textStatus.label}</span>
                    {status === bidStatusesEnum.Done && !loadUnloadedAtByLogist && !autoLeftAt && !loadUnloadedAt && (
                        <>
                            <br />
                            <span className="badge badge-warning">Без выезда</span>
                        </>
                    )}
                    {isEmptyMileageBid && (
                        <>
                            <br />
                            <span className="badge badge-warning">Порожняя</span>
                        </>
                    )}
                    {this.props.organization.usesBidsApproval &&
                        !isEmptyMileageBid &&
                        status === bidStatusesEnum.New &&
                        isCreatedByOperator && (
                            <>
                                <br />
                                {isApproved ? (
                                    <span className="badge badge-success">Согласована</span>
                                ) : (
                                    <span className="badge badge-warning">Не согласована</span>
                                )}
                            </>
                        )}
                    {acceptedByDriverAt && (
                        <>
                            <br />
                            <Tooltip overlay={dateTimeToView(acceptedByDriverAt)} placement="top">
                                <span className="badge badge-info">Принята водителем</span>
                            </Tooltip>
                        </>
                    )}
                    {isPreBid && (
                        <>
                            <br />
                            <span className="badge badge-secondary">Предзаявка</span>
                        </>
                    )}
                </>
            );
        }
    };

    renderPoint = (point) => {
        const factEnteredAt = point.enteredAtByLogist || point.autoEnteredAt;
        const time = dateTimeToView(point.planEnterDateOffset);
        const utcTimezone = formatDateTimezone(point);
        const { showFullAddress, showFactDates } = this.props;
        const address = getShortAddress(point.geozone, showFullAddress);
        return (
            <>
                <span className="clearfix">
                    <Address address={address} />
                </span>
                <small className="text-muted">
                    План: {time} {utcTimezone}
                    {showFactDates && factEnteredAt && (
                        <>
                            <br />
                            Факт: {dateTimeToView(factEnteredAt)} {utcTimezone}
                        </>
                    )}
                </small>
            </>
        );
    };

    showBid = () => {
        const { id } = this.props.item;
        return this.props.history.push(paths.bidShow("bid", id));
    };

    renderTemperatureRegimeInfo = () => {
        const { availableFeatureModules } = this.props.profile;
        if (!availableFeatureModules.includes(featureModuleTypesEnum.TemperatureRegime)) {
            return null;
        }
        const { intStateFlags, temperatureMinimum, temperatureMaximum } = this.props.item;
        if (temperatureMinimum == null && temperatureMaximum == null) {
            return null;
        }
        const carTemperatureRegimeDeviated =
            (intStateFlags & carTemperatureRegimeDeviatedFlagVal) === carTemperatureRegimeDeviatedFlagVal;
        const trailerTemperatureRegimeDeviated =
            (intStateFlags & trailerTemperatureRegimeDeviatedFlagVal) === trailerTemperatureRegimeDeviatedFlagVal;
        const temperatureRange = this.formatTemperatureRegimeRange(temperatureMinimum, temperatureMaximum);
        const hasTemperatureDeviation = carTemperatureRegimeDeviated || trailerTemperatureRegimeDeviated;
        const messageData = hasTemperatureDeviation
            ? {
                  text: this.getTemperatureDeviationText(
                      carTemperatureRegimeDeviated,
                      trailerTemperatureRegimeDeviated,
                  ),
                  className: "badge badge-danger badge-pill mr-1",
              }
            : {
                  text: "Температурный режим в норме",
                  className: "badge badge-success badge-pill mr-1",
              };
        const overlay = (
            <div className="text-left">
                <div>{messageData.text}</div>
                {temperatureRange && <div>Диапазон: {temperatureRange}</div>}
                {this.renderLastTemperatureValue()}
            </div>
        );
        return (
            <Tooltip overlay={overlay} overlayClassName="notifys__tooltip" placement="top">
                <span className={messageData.className}>
                    {this.renderTemperatureRange(temperatureMinimum, temperatureMaximum)}
                </span>
            </Tooltip>
        );
    };

    formatTemperatureValue = (value) => `${formatNumber(value)}℃`;

    formatTemperatureRegimeRange = (temperatureMinimum, temperatureMaximum) => {
        if (temperatureMinimum != null && temperatureMaximum != null) {
            return `от ${this.formatTemperatureValue(temperatureMinimum)} до ${this.formatTemperatureValue(
                temperatureMaximum,
            )}`;
        }
        if (temperatureMinimum != null) {
            return `от ${this.formatTemperatureValue(temperatureMinimum)}`;
        }
        if (temperatureMaximum != null) {
            return `до ${this.formatTemperatureValue(temperatureMaximum)}`;
        }
        return null;
    };

    getTemperatureDeviationText = (carTemperatureRegimeDeviated, trailerTemperatureRegimeDeviated) => {
        if (carTemperatureRegimeDeviated && trailerTemperatureRegimeDeviated) {
            return "Отклонение или нет данных температуры по трекерам машины и прицепа";
        }
        if (carTemperatureRegimeDeviated) {
            return "Отклонение или нет данных температуры по трекеру машины";
        }
        if (trailerTemperatureRegimeDeviated) {
            return "Отклонение или нет данных температуры по трекеру прицепа";
        }
        return "Температурный режим в норме";
    };

    renderTemperatureRange = (temperatureMinimum, temperatureMaximum) => {
        if (temperatureMinimum != null && temperatureMaximum != null) {
            return (
                <>
                    {this.formatTemperatureValue(temperatureMinimum)} &mdash;{" "}
                    {this.formatTemperatureValue(temperatureMaximum)}
                </>
            );
        }
        return this.formatTemperatureRegimeRange(temperatureMinimum, temperatureMaximum);
    };

    renderLastTemperatureValue = () => {
        const { lastTemperatureValue } = this.props.item;
        if (!lastTemperatureValue) {
            return null;
        }
        const { fixedAt } = lastTemperatureValue;
        const values = lastTemperatureValue.values || [];
        return (
            <>
                <div className="font-weight-bold mt-1">Текущая температура</div>
                {values.length > 0
                    ? values.map((value, index) => (
                          <div key={index}>
                              Датчик №{index + 1}: {this.formatTemperatureValue(value)}
                          </div>
                      ))
                    : "Нет данных"}
                {fixedAt && <i className="small">обновлено в {dateTimeToView(fixedAt, "DD.MM.YYYY HH:mm (Z)")}</i>}
            </>
        );
    };

    renderRefuelingInfo = () => {
        const { intStateFlags } = this.props.item;
        const hasPlannedRefuelings = (intStateFlags & hasPlannedRefuelingsFlagVal) === hasPlannedRefuelingsFlagVal;
        const maxNumberOfRefuelingsReached =
            (intStateFlags & maxNumberOfRefuelingsReachedVal) === maxNumberOfRefuelingsReachedVal;
        if (!hasPlannedRefuelings) {
            return null;
        }
        return (
            <Tooltip
                overlay={enumLabel(
                    bidStateFlags,
                    maxNumberOfRefuelingsReached
                        ? bidStateFlagsEnum.MaxNumberOfRefuelingsReached
                        : bidStateFlagsEnum.HasPlannedRefuelings,
                )}
                placement="top"
            >
                <i
                    className={cn("pr-1 icon-uEA36-azs", {
                        "text-info": !maxNumberOfRefuelingsReached,
                        "text-danger": maxNumberOfRefuelingsReached,
                    })}
                />
            </Tooltip>
        );
    };

    toggleSelect = (e) => {
        e.stopPropagation();
        const { id, status, lastBidPoint } = this.props.item;
        this.props.toggleItem({ id, status, lastBidPoint });
    };

    openExternalDistributionBid = (e) => {
        e.stopPropagation();
        const { carId } = this.props.item;
        const { organization } = this.props;
        ymEvent("ExternalDisBids");
        return this.props.modal({
            name: "externalDistributionBid",
            title: "Информация по внешнему заказу",
            windowMod: "wide",
            carId,
            organization,
        });
    };

    renderDriver = () => {
        const { drivers, isDriverForwarder } = this.props.item;
        if (!drivers) {
            return null;
        }

        const driverForwarderBadge = isDriverForwarder ? (
            <span className="badge badge-info ml-1">Водитель-экспедитор</span>
        ) : null;

        if (drivers.length > 2) {
            return (
                <div>
                    {drivers[0].shortName} &rarr;&nbsp;
                    <Tooltip
                        overlay={drivers
                            .slice(1, -1)
                            .map((item) => item.shortName)
                            .join(" → ")}
                        placement="top"
                    >
                        <span className="circle">{drivers.length - 2}</span>
                    </Tooltip>
                    &nbsp;&rarr;&nbsp;
                    {drivers[drivers.length - 1].shortName}
                    {driverForwarderBadge}
                </div>
            );
        } else {
            return (
                <div>
                    {drivers.map((item) => item.shortName).join(" → ")}
                    {driverForwarderBadge}
                </div>
            );
        }
    };

    renderSecondaryDriver = () => {
        const { secondaryDriverName } = this.props.item;
        if (!secondaryDriverName) {
            return null;
        }
        return (
            <Tooltip overlay="Второй водитель" placement="bottomLeft">
                <div className="text-muted">{secondaryDriverName}</div>
            </Tooltip>
        );
    };

    renderTruck = () => {
        const { cars, car } = this.props.item;
        const { trackerId } = car || {};
        if (!cars) {
            return null;
        }

        const lastCar = cars[cars.length - 1];
        const renderLastCar = () => (
            <>
                {trackerId && (
                    <Tooltip overlay="К машине привязан трекер" placement="top">
                        <span className="text-success">
                            <i className="icon-uEA71-map-marked pr-1" />
                        </span>
                    </Tooltip>
                )}
                {lastCar?.number}
            </>
        );

        if (cars.length > 1) {
            const firstCar = cars[0];
            return (
                <div>
                    {firstCar.number} &rarr;&nbsp;
                    {cars.length > 2 && (
                        <>
                            <Tooltip
                                overlay={cars
                                    .slice(1, -1)
                                    .map((item) => item.number)
                                    .join(" → ")}
                                placement="top"
                            >
                                <span className="circle">{cars.length - 2}</span>
                            </Tooltip>
                            &nbsp;&rarr;&nbsp;
                        </>
                    )}
                    {renderLastCar()}
                </div>
            );
        } else {
            return <div>{renderLastCar()}</div>;
        }
    };

    renderTrailer = () => {
        const { trailers } = this.props.item;
        if (!trailers?.length) {
            return null;
        }

        const lastTrailer = trailers[trailers.length - 1];
        if (trailers.length > 1) {
            const firstTrailer = trailers[0];
            return (
                <div>
                    {firstTrailer.number} &rarr;&nbsp;
                    {trailers.length > 2 && (
                        <>
                            <Tooltip
                                overlay={trailers
                                    .slice(1, -1)
                                    .map((item) => item.number)
                                    .join(" → ")}
                                placement="top"
                            >
                                <span className="circle">{trailers.length - 2}</span>
                            </Tooltip>
                            &nbsp;&rarr;&nbsp;
                        </>
                    )}
                    {lastTrailer?.number}
                </div>
            );
        } else {
            return <div>{lastTrailer?.number}</div>;
        }
    };

    formatRatePerKm = (rate) => {
        const { organization, roles } = this.props;

        return `${hiddenUserData(
            formatNumber(rate, { style: "currency", currency: organization?.currency }),
            { type: "pricePerVal" },
            roles.isTruckingOrganizationDispatcher,
        )}/км`;
    };

    getRateComparisonInfo = () => {
        const { averageRateByDestination, averageRate } = this.props.item;

        if (averageRate == null || averageRateByDestination == null || averageRateByDestination <= 0) {
            return null;
        }

        const deviation = ((averageRate - averageRateByDestination) / averageRateByDestination) * 100;

        if (!Number.isFinite(deviation)) {
            return null;
        }

        const roundedDeviationValue = Math.round(deviation * 10) / 10 || 0;
        const roundedDeviation = formatNumber(roundedDeviationValue, { maximumFractionDigits: 1 });
        const visibleDeviation = `${roundedDeviationValue > 0 ? "+" : ""}${roundedDeviation}%`;
        const isInMarket = Math.abs(deviation) <= MARKET_RATE_THRESHOLD_PERCENT;
        const isHigher = roundedDeviationValue > 0;
        const statusText = isInMarket
            ? "В рынке"
            : `${isHigher ? "Выше" : "Ниже"} рынка на ${formatNumber(Math.abs(roundedDeviationValue), {
                  maximumFractionDigits: 1,
              })}%`;
        const tooltip = (
            <div className="text-left">
                <div className="font-weight-bold mb-1">Сравнение с медианной ставкой по направлению</div>
                <div>Ставка заявки: {this.formatRatePerKm(averageRate)}</div>
                <div>Медианная ставка: {this.formatRatePerKm(averageRateByDestination)}</div>
                {isInMarket && <div>Отклонение: {visibleDeviation}</div>}
                <div className={isInMarket ? "text-muted" : isHigher ? "text-success" : "text-danger"}>
                    {statusText}
                </div>
            </div>
        );

        return {
            text: `(${visibleDeviation})`,
            textClassName: roundedDeviationValue >= 0 ? "text-success" : "text-danger",
            tooltip,
        };
    };

    renderAverageRate = () => {
        const { averageRate } = this.props.item;

        if (averageRate == null) {
            return null;
        }

        return <div>{this.formatRatePerKm(averageRate)}</div>;
    };

    renderRateComparisonInfo = () => {
        const comparison = this.getRateComparisonInfo();

        if (!comparison) {
            return null;
        }

        const content = (
            <span
                className={cn("b-table__rate-comparison", comparison.textClassName, {
                    "b-table__rate-comparison--tooltip": comparison.tooltip,
                })}
            >
                {comparison.text}
            </span>
        );

        if (!comparison.tooltip) {
            return <div>{content}</div>;
        }

        return (
            <Tooltip overlay={comparison.tooltip} overlayClassName="notifys__tooltip" placement="top">
                <div>{content}</div>
            </Tooltip>
        );
    };

    renderWaybillsStatus = () => {
        const { waybills, hasWaybills } = this.props.item;
        if (Array.isArray(waybills) && waybills.length > 0) {
            return waybills.map((waybill, index) => {
                const info = getWaybillTitleStatusInfo(waybill);
                if (!info) {
                    const overlay = waybill.billNumber ? `ЭТрН №${waybill.billNumber}` : "ЭТрН";
                    return (
                        <Tooltip
                            key={waybill.id || index}
                            overlay={overlay}
                            overlayClassName="notifys__tooltip"
                            placement="bottom"
                        >
                            <i className="icon-uEABB-file-signature b-table__compound-icon mr-1" />
                        </Tooltip>
                    );
                }
                const overlay = waybill.billNumber
                    ? `ЭТрН №${waybill.billNumber}: ${info.statusText}`
                    : `ЭТрН: ${info.statusText}`;
                const badgeClass = cn("badge mr-1", {
                    "badge-danger": info.isRed,
                    "badge-success": info.isGreen,
                });
                return (
                    <Tooltip
                        key={waybill.id || index}
                        overlay={overlay}
                        overlayClassName="notifys__tooltip"
                        placement="bottom"
                    >
                        <span className={badgeClass}>{info.badgeText}</span>
                    </Tooltip>
                );
            });
        }
        if (hasWaybills) {
            return (
                <Tooltip overlay="ЭТрН" overlayClassName="notifys__tooltip" placement="bottom">
                    <i className="icon-uEABB-file-signature b-table__compound-icon mr-1" />
                </Tooltip>
            );
        }
        return null;
    };

    render() {
        const {
            item,
            index,
            params,
            selected,
            factoringEnabled,
            organization,
            showAllPointAddresses,
            showLogistColumn,
            showOrganizationColumn,
            className,
            organizationTimezone,
        } = this.props;
        const {
            cargos,
            id,
            estimations,
            status,
            price,
            priceWithoutCosts,
            payment,
            counterparty,
            firstBidPoint,
            lastBidPoint,
            externalId,
            isCompoundBid,
            isClosedByLogist,
            lastSyncStatus,
            isExpressBid,
            intStateFlags,
            hasItemsChange,
            hasFactoring,
            hasExternalBid,
            externalBidOutput,
            pointAddresses,
            sharedAccess,
            secondaryDriverId,
            carLogistName,
            carTransportColumnName,
            legalPersonName,
            hasServicePoints,
        } = item;
        const displayedPrice = this.props.showCostAwareValues ? (priceWithoutCosts ?? price) : price;

        const latePeriod = estimations && estimations.length !== 0 ? estimations[0].latePeriod : null;
        const { Started } = bidStatusesEnum;
        const cnames = cn(className, {
            "b-table__late-row": latePeriod && latePeriod.charAt(0) === "-" && Started === status,
            "b-table__selected-row": selected,
        });
        const hasUploadedFile = (intStateFlags & hasUploadedFileFlagVal) === hasUploadedFileFlagVal;
        const hasAutoCorrectedPoint = (intStateFlags & hasAutoCorrectedPointFlagVal) === hasAutoCorrectedPointFlagVal;
        const { isTruckingOrganizationDispatcher } = this.props.roles;
        return (
            <tr className={cnames} onClick={this.showBid}>
                {factoringEnabled && (
                    <td>
                        <div className="px-2 pb-2" onClick={this.toggleSelect}>
                            <span className={cn("checkbox", { "checkbox--checked": selected })} />
                        </div>
                    </td>
                )}
                <td>{params.start + index + 1}</td>
                <td>
                    <small>
                        <Link className="mr-1" to={paths.bidShow("bid", id)}>
                            {id}
                        </Link>
                        {isCompoundBid && (
                            <Tooltip overlay="Сборная заявка" placement="bottom">
                                <i className="mr-1 b-table__compound-icon icon-uEA76-animation-outline" />
                            </Tooltip>
                        )}
                        {isClosedByLogist && (
                            <Tooltip overlay="Закрыта вручную" placement="bottom">
                                <i className="mr-1 b-table__compound-icon icon-uEA83-user-edit" />
                            </Tooltip>
                        )}
                        {isExpressBid && (
                            <Tooltip overlay="Экспресс-заявка" placement="bottom">
                                <i className="mr-1 icon-uEA90-bolt b-table__compound-icon" />
                            </Tooltip>
                        )}
                        {hasUploadedFile && (
                            <Tooltip
                                overlay={enumLabel(bidStateFlags, bidStateFlagsEnum.HasUploadedFile)}
                                overlayClassName="notifys__tooltip"
                                placement="bottom"
                            >
                                <i className="icon-uEA0A-file b-table__compound-icon mr-1" />
                            </Tooltip>
                        )}
                        {hasItemsChange && (
                            <Tooltip
                                overlay="В заявке была осуществлена перецепка/пересменка"
                                overlayClassName="notifys__tooltip"
                                placement="bottom"
                            >
                                <i className="icon-uEAB3-arrows-rotate b-table__compound-icon mr-1" />
                            </Tooltip>
                        )}
                        {hasFactoring && (
                            <Tooltip
                                overlay="Заявка отправлена на факторинг"
                                overlayClassName="notifys__tooltip"
                                placement="bottom"
                            >
                                <i className="icon-uEAA2-factoring b-table__compound-icon mr-1" />
                            </Tooltip>
                        )}
                        {this.renderWaybillsStatus()}
                        {hasExternalBid && (
                            <div
                                className="text-nowrap d-flex align-items-center b-table__external-bid-icon--wrap mr-1"
                                onClick={this.openExternalDistributionBid}
                            >
                                <i className="icon-uEAB5-truck-cost pr-2 b-table__external-bid-icon" />
                                {externalBidOutput && (
                                    <span className="b-table__external-bid-text">
                                        от {formatNumber(externalBidOutput)} {organization?.currencyData?.name}/км
                                    </span>
                                )}
                            </div>
                        )}
                        {sharedAccess && (
                            <Tooltip
                                overlay="Контрагенту предоставлен доступ"
                                overlayClassName="notifys__tooltip"
                                placement="bottom"
                            >
                                <i className="icon-uEA49-eye b-table__compound-icon mr-1 text-primary" />
                            </Tooltip>
                        )}
                        {secondaryDriverId && (
                            <Tooltip overlay="Парный экипаж" overlayClassName="notifys__tooltip" placement="bottom">
                                <i className="icon-uEAC2-user-group b-table__compound-icon mr-1" />
                            </Tooltip>
                        )}
                        {hasAutoCorrectedPoint && (
                            <Tooltip
                                overlay="Точка заявки скорректирована автоматически"
                                overlayClassName="notifys__tooltip"
                                placement="bottom"
                            >
                                <i className="icon-uEA82-route b-table__compound-icon mr-1" />
                            </Tooltip>
                        )}
                        {hasServicePoints && (
                            <Tooltip
                                overlay="В заявке присутствуют сервисные точки"
                                overlayClassName="notifys__tooltip"
                                placement="bottom"
                            >
                                <i className="icon-uEA97-wrench b-table__compound-icon mr-1" />
                            </Tooltip>
                        )}
                    </small>
                    <SyncStatusInfo
                        id={id}
                        externalId={externalId}
                        lastSyncStatus={lastSyncStatus}
                        entityType={syncEntityTypesEnum.Bid}
                        organizationTimezone={organizationTimezone}
                    />
                </td>
                <td className="b-table__point-cell">
                    {firstBidPoint && Object.keys(firstBidPoint).length > 0 && this.renderPoint(firstBidPoint)}
                </td>
                {showAllPointAddresses && (
                    <td className="b-table__cargo-cell">
                        {pointAddresses?.length > 2 ? (
                            <ul className="pl-2">
                                {pointAddresses.slice(1, -1).map((item, i) => (
                                    <li key={i} className="small">
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <>&mdash;</>
                        )}
                    </td>
                )}
                <td className="b-table__point-cell">
                    {lastBidPoint && Object.keys(lastBidPoint).length > 0 && this.renderPoint(lastBidPoint)}
                </td>
                <td className="b-table__cargo-cell">
                    <small>
                        {cargos && cargos.length > 0 && cargosWithTemperature(cargos)}
                        {this.renderTemperatureRegimeInfo()}
                        {this.renderRefuelingInfo()}
                        {this.renderTruck()}
                        {this.renderDriver()}
                        {this.renderSecondaryDriver()}
                        {this.renderTrailer()}
                        {this.renderParticipants()}
                    </small>
                </td>
                {showLogistColumn && (
                    <td className="b-table__cargo-cell">
                        {carLogistName && (
                            <div>
                                <small>{carLogistName}</small>
                            </div>
                        )}
                        {carTransportColumnName && (
                            <div>
                                <small className="text-muted">{carTransportColumnName}</small>
                            </div>
                        )}
                    </td>
                )}
                <td className="b-table__price-cell">
                    <div className="small mb-1">
                        {hiddenUserData(
                            formatNumber(displayedPrice, { style: "currency", currency: organization?.currency }),
                            { type: "price" },
                            isTruckingOrganizationDispatcher,
                        )}
                        {this.renderAverageRate()}
                        {this.renderRateComparisonInfo()}
                    </div>
                    <>
                        <div className="small">
                            {hiddenUserData(counterparty?.name, { type: "title" }, isTruckingOrganizationDispatcher)}
                        </div>
                        {counterparty?.insolvencyRegistryEntry &&
                            counterparty?.insolvencyRegistryEntry.insolvencyRank > 0 && (
                                <HiddenDataWrapper>
                                    <div className="small">
                                        {counterpartyInsolvencyTooltip(
                                            counterparty.insolvencyRegistryEntry,
                                            this.props.modal,
                                        )}
                                    </div>
                                </HiddenDataWrapper>
                            )}
                    </>
                    {!!payment?.paymentStatus && (
                        <div>
                            <span
                                className={cn("badge badge-pill", {
                                    "badge-primary": bidPaymentStatusesEnum.PartiallyPaid === payment.paymentStatus,
                                    "badge-secondary": bidPaymentStatusesEnum.NotPaid === payment.paymentStatus,
                                    "badge-success": bidPaymentStatusesEnum.Paid === payment.paymentStatus,
                                })}
                            >
                                {hiddenUserData(enumLabel(bidPaymentStatuses, payment.paymentStatus))}
                            </span>
                            {payment.isPaymentOverdue && (
                                <span className="badge badge-pill badge-overdue ml-1">
                                    Просрочен
                                </span>
                            )}
                        </div>
                    )}
                </td>
                {showOrganizationColumn && (
                    <td className="b-table__cargo-cell">{legalPersonName && <small>{legalPersonName}</small>}</td>
                )}
                <td>{this.renderStatus()}</td>
            </tr>
        );
    }
}
